import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { DeviceModel, IDevice } from '../models/device.model';
import { ISession, SessionModel } from '../models/session.model';
import { Profile } from '../models/profile.model';
import { IServerResponse, calculateTotalPages } from '../models/server-response.model';
import { IUser, UserModel } from '../models/user.model';
import { IUserListItem, UserListItemModel } from '../models/user-list-item.model';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { UtilService } from './util.service';

export interface IUsersFilters {
  search?: string;
  profile?: Profile;
  status?: boolean;
  /**
   * `true` = solo usuarios YA registrados en la base de datos, `false` = solo
   * los que aun viven exclusivamente en Firebase Auth (version anterior de
   * KeyStoreMan), `undefined` = ambos.
   */
  registered?: boolean;
  page?: number;
  pageSize?: number;
}

/** Avance de la migracion de Firebase Auth a la base de datos. */
export interface IUsersSummary {
  total: number;
  registered: number;
  unregistered: number;
  /**
   * Cuantos de los usuarios YA REGISTRADOS (`registered` de arriba) aceptaron
   * tanto los Terminos y condiciones como el Aviso de privacidad.
   *
   * OJO: a 2026-09-15 el endpoint `GET /users` de `keystoreman-console-api`
   * TODAVIA NO calcula ni devuelve este conteo (se reviso `user_controller.py`
   * y `UsersSummaryModel` solo trae `total`/`registered`/`unregistered`) — el
   * nombre `acceptedBoth` es una propuesta de este agente para cuando el API
   * lo agregue, no un campo confirmado. Opcional/nulo a proposito: mientras
   * no llegue, el panel de aceptacion legal del dashboard cae en su estado
   * "sin datos disponibles", igual que el resto de los KPIs cuando falta el
   * dato.
   */
  acceptedBoth?: number | null;
}

export interface IUserUpdateRequest {
  names: string;
  lastNames: string;
  profile: Profile;
  status?: boolean;
}

/**
 * Usuarios de KeyStoreMan.
 *
 * OJO: el API NO expone POST ni DELETE de usuarios. Las altas ocurren en la app
 * movil; desde la consola solo se editan.
 *
 * `GET /users` devuelve la UNION de Firebase Auth + MySQL: la version anterior
 * de la app solo creaba la cuenta en Firebase, y la nueva ademas la registra
 * en base de datos. `users` (el listado) usa `UserListItemModel`, que
 * contempla ambos casos; `user` (la ficha de detalle, `GET /users/{uid}`)
 * sigue usando `UserModel` sin cambios, porque esa ruta solo existe para
 * quien SI esta en la base de datos.
 *
 * Convencion comun a todos los servicios: los GET de listado usan el `loading`
 * local (skeletons) y las mutaciones el spinner global; el `statusCode` del
 * envelope se revisa SIEMPRE y su `message` se muestra tal cual, tanto en el
 * exito como en el error.
 */
@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly util = inject(UtilService);
  private readonly spinner = inject(SpinnerService);
  private readonly toast = inject(ToastService);
  private readonly urlApi = `${environment.urlApi}/users`;

  /** `loading` local: alimenta los skeletons de las listas, NO el spinner global. */
  readonly loading = signal(false);
  readonly loadingDevices = signal(false);
  readonly loadingSessions = signal(false);

  readonly users = signal<UserListItemModel[]>([]);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pageSize = signal(20);
  readonly totalPages = computed(() => calculateTotalPages(this.total(), this.pageSize()));

  /**
   * Avance de la migracion (`data.summary` del envelope): cuantos de los
   * usuarios devueltos por la ULTIMA llamada a `getUsers` ya estan en base de
   * datos. `null` mientras no se ha cargado o si la carga fallo — igual que
   * `usersError`, para no enseñar un 0 % que en realidad es "no se sabe".
   */
  readonly usersSummary = signal<IUsersSummary | null>(null);

  readonly user = signal<UserModel | null>(null);
  readonly devices = signal<DeviceModel[]>([]);

  /** Sesiones del usuario en detalle: llevan su propia paginacion. */
  readonly sessions = signal<SessionModel[]>([]);
  readonly sessionsTotal = signal(0);
  readonly sessionsPage = signal(1);
  readonly sessionsPageSize = signal(10);
  readonly sessionsTotalPages = computed(() =>
    calculateTotalPages(this.sessionsTotal(), this.sessionsPageSize()),
  );

  /**
   * `loaded` = ya termino la primera peticion (con exito o con error), asi que
   * YA se sabe si hay datos. `loading` solo dice que hay una peticion en vuelo,
   * y arranca en `false`: sin `loaded` el primer render enseñaria el estado
   * vacio antes de que la carga hubiera empezado siquiera.
   */
  readonly usersLoaded = signal(false);
  readonly devicesLoaded = signal(false);
  readonly sessionsLoaded = signal(false);

  /** Mensaje del ultimo fallo de carga, o `null` si la carga salio bien. */
  readonly usersError = signal<string | null>(null);
  readonly userError = signal<string | null>(null);
  readonly devicesError = signal<string | null>(null);
  readonly sessionsError = signal<string | null>(null);

  async getUsers(filters: IUsersFilters = {}): Promise<UserListItemModel[]> {
    this.loading.set(true);
    try {
      const params = this.util.buildParams({
        search: filters.search,
        profile: filters.profile,
        status: filters.status,
        registered: filters.registered,
        page: filters.page,
        page_size: filters.pageSize,
      });
      const response = await firstValueFrom(
        this.http.get<
          IServerResponse<{
            users: IUserListItem[];
            total: number;
            page: number;
            pageSize: number;
            summary: IUsersSummary;
          }>
        >(this.urlApi, { params }),
      );

      if (response.statusCode !== 200) {
        this.users.set([]);
        this.total.set(0);
        this.usersSummary.set(null);
        this.usersError.set(response.message);
        this.toast.error(response.message);
        return [];
      }

      const users = (response.data?.users ?? []).map((user) => new UserListItemModel(user));
      this.users.set(users);
      this.total.set(response.data?.total ?? 0);
      this.page.set(response.data?.page ?? 1);
      this.pageSize.set(response.data?.pageSize ?? 20);
      this.usersSummary.set(response.data?.summary ?? null);
      this.usersError.set(null);
      return users;
    } catch (error) {
      this.users.set([]);
      this.total.set(0);
      this.usersSummary.set(null);
      const message = this.util.errorMessage(error, 'No fue posible cargar los usuarios.');
      this.usersError.set(message);
      this.toast.error(message);
      return [];
    } finally {
      this.loading.set(false);
      this.usersLoaded.set(true);
    }
  }

  async getUser(uid: string): Promise<UserModel | null> {
    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.get<IServerResponse<{ user: IUser }>>(`${this.urlApi}/${uid}`),
      );

      if (response.statusCode !== 200) {
        this.user.set(null);
        this.userError.set(response.message);
        this.toast.error(response.message);
        return null;
      }

      const user = response.data?.user ? new UserModel(response.data.user) : null;
      this.user.set(user);
      this.userError.set(null);
      return user;
    } catch (error) {
      this.user.set(null);
      const message = this.util.errorMessage(error, 'No fue posible cargar el usuario.');
      this.userError.set(message);
      this.toast.error(message);
      return null;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * PUT /users/{uid}. Devuelve 400 si el perfil no existe o esta inactivo: ese
   * error llega dentro del envelope, por eso se lee el cuerpo en vez de dejarlo
   * escapar como excepcion.
   */
  async updateUser(uid: string, request: IUserUpdateRequest): Promise<UserModel | null> {
    this.spinner.show('Guardando usuario...');
    try {
      const response = await firstValueFrom(
        this.http.put<IServerResponse<{ user: IUser }>>(`${this.urlApi}/${uid}`, request),
      );

      if (response.statusCode !== 200) {
        this.toast.error(response.message);
        return null;
      }

      const user = response.data?.user ? new UserModel(response.data.user) : null;
      if (user) this.user.set(user);
      this.toast.success(response.message);
      return user;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No fue posible actualizar el usuario.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  /** GET /users/{uid}/devices - sin paginacion. */
  async getDevices(uid: string): Promise<DeviceModel[]> {
    this.loadingDevices.set(true);
    try {
      const response = await firstValueFrom(
        this.http.get<IServerResponse<{ devices: IDevice[] }>>(`${this.urlApi}/${uid}/devices`),
      );

      if (response.statusCode !== 200) {
        this.devices.set([]);
        this.devicesError.set(response.message);
        this.toast.error(response.message);
        return [];
      }

      const devices = (response.data?.devices ?? []).map((device) => new DeviceModel(device));
      this.devices.set(devices);
      this.devicesError.set(null);
      return devices;
    } catch (error) {
      this.devices.set([]);
      const message = this.util.errorMessage(error, 'No fue posible cargar los dispositivos.');
      this.devicesError.set(message);
      this.toast.error(message);
      return [];
    } finally {
      this.loadingDevices.set(false);
      this.devicesLoaded.set(true);
    }
  }

  async getSessions(
    uid: string,
    filters: { active?: boolean; page?: number; pageSize?: number } = {},
  ): Promise<SessionModel[]> {
    this.loadingSessions.set(true);
    try {
      const params = this.util.buildParams({
        active: filters.active,
        page: filters.page,
        page_size: filters.pageSize,
      });
      const response = await firstValueFrom(
        this.http.get<
          IServerResponse<{ sessions: ISession[]; total: number; page: number; pageSize: number }>
        >(`${this.urlApi}/${uid}/sessions`, { params }),
      );

      if (response.statusCode !== 200) {
        this.sessions.set([]);
        this.sessionsTotal.set(0);
        this.sessionsError.set(response.message);
        this.toast.error(response.message);
        return [];
      }

      const sessions = (response.data?.sessions ?? []).map((session) => new SessionModel(session));
      this.sessions.set(sessions);
      this.sessionsTotal.set(response.data?.total ?? 0);
      this.sessionsPage.set(response.data?.page ?? 1);
      this.sessionsPageSize.set(response.data?.pageSize ?? 10);
      this.sessionsError.set(null);
      return sessions;
    } catch (error) {
      this.sessions.set([]);
      this.sessionsTotal.set(0);
      const message = this.util.errorMessage(error, 'No fue posible cargar las sesiones.');
      this.sessionsError.set(message);
      this.toast.error(message);
      return [];
    } finally {
      this.loadingSessions.set(false);
      this.sessionsLoaded.set(true);
    }
  }
}
